import { DropdownMenuItem, DropdownMenuSeparator } from '@components/atoms/DropdownMenu'
import { SplitButton, type SplitButtonProps } from '@components/molecules/SplitButton'
import Icon from '@components/utils/Icon'

import type { ComponentDoc, PropDocs } from '../types'

const props: PropDocs<SplitButtonProps> = {
  label: { type: 'ReactNode', required: true, description: 'The main button text.' },
  onClick: { type: '() => void', required: true, description: 'The main action.' },
  leadingIcon: { type: 'IconTypes', description: 'An icon before the label.' },
  ariaLabel: {
    type: 'string',
    description: 'A fuller name for the main button, for example "Call Maria Rossi" when every row says "Call".',
  },
  menuAriaLabel: {
    type: 'string',
    required: true,
    description: 'Names the chevron button, for example "More actions for Maria Rossi".',
  },
  children: { type: 'ReactNode', required: true, description: 'The menu items. Use DropdownMenuItem and friends.' },
  rank: { type: "'primary' | 'secondary' | 'link'", default: "'primary'", description: 'As on Button.' },
  color: { type: "'primary' | 'danger' | 'success' | 'warning'", default: "'primary'", description: 'As on Button.' },
  size: { type: "'sm' | 'md' | 'xl' | '2xl'", default: "'md'", description: 'As on Button.' },
  className: { type: 'string', description: 'Extra classes on the wrapper.' },
  modal: {
    type: 'boolean',
    default: 'true',
    description:
      'As on DropdownMenu. Pass false in a table row, so tooltips on the row still close when the pointer leaves them while the menu is open.',
  },
}

const menu = (
  <>
    <DropdownMenuItem>
      <Icon type="clipboard-minus" className="tw:text-gray-500" />
      Log contact
    </DropdownMenuItem>
    <DropdownMenuItem>
      <Icon type="message-chat-square" className="tw:text-gray-500" />
      SMS
    </DropdownMenuItem>
    <DropdownMenuSeparator />
    <DropdownMenuItem>
      <Icon type="check-circle" className="tw:text-gray-500" />
      Mark as done
    </DropdownMenuItem>
  </>
)

const doc: ComponentDoc<SplitButtonProps> = {
  slug: 'split-button',
  name: 'SplitButton',
  tier: 'molecule',
  description:
    'A button with a chevron beside it. The button does the main action. The chevron opens a menu of the other actions.',
  importStatement: "import { SplitButton } from '@components/molecules/SplitButton'",
  sourcePath: 'app/frontend/components/molecules/SplitButton.tsx',
  component: SplitButton,
  props,
  examples: [
    {
      title: 'A row action',
      note: 'Secondary and small, as on the callback list.',
      render: () => (
        <SplitButton
          rank="secondary"
          size="sm"
          leadingIcon="phone-call-01"
          label="Call"
          ariaLabel="Call Maria Rossi"
          menuAriaLabel="More actions for Maria Rossi"
          onClick={() => {}}
        >
          {menu}
        </SplitButton>
      ),
      code: `<SplitButton
  rank="secondary"
  size="sm"
  leadingIcon="phone-call-01"
  label="Call"
  ariaLabel={\`Call \${supporter.fullName}\`}
  menuAriaLabel={\`More actions for \${supporter.fullName}\`}
  onClick={call}
>
  <DropdownMenuItem onSelect={logContact}>Log contact</DropdownMenuItem>
  <DropdownMenuItem onSelect={sms}>SMS</DropdownMenuItem>
</SplitButton>`,
    },
    {
      title: 'Primary',
      render: () => (
        <SplitButton label="Save" menuAriaLabel="More ways to save" onClick={() => {}}>
          <DropdownMenuItem>Save and add another</DropdownMenuItem>
          <DropdownMenuItem>Save as draft</DropdownMenuItem>
        </SplitButton>
      ),
      code: `<SplitButton label="Save" menuAriaLabel="More ways to save" onClick={save}>
  <DropdownMenuItem onSelect={saveAndAdd}>Save and add another</DropdownMenuItem>
  <DropdownMenuItem onSelect={saveDraft}>Save as draft</DropdownMenuItem>
</SplitButton>`,
    },
  ],
  usage: {
    do: [
      'Use it when one action is far more common than the rest.',
      'Put the other actions in the menu, so the row needs no separate ⋮ button.',
    ],
    dont: ['Use it when no action stands out. Use a plain DropdownMenu.', 'Hide the main action in the menu as well.'],
  },
  a11y: [
    'Two buttons. The chevron gets its own name from menuAriaLabel.',
    'The menu opens with Enter, Space or the down arrow. Escape closes it and returns focus to the chevron.',
  ],
}

export default doc
